import {Box, Grid, Paper, styled, Typography} from '@mui/material';
import type {PersonalizeSearchType} from './PersonalizeSearch';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import {categories} from './constants';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  OrangeLeftArrowCircle,
  OrangeRightArrowCircle,
} from '@jobseeker/lotus/assets/icons';
import {useIsCustomWidth} from '@jobseeker/lotus/hooks';
import {useEffect, useState} from 'react';

type PersonalizeHeaderType = PersonalizeSearchType & {
  isMobile: boolean;
  onSelect: (item: string | number | undefined) => void;
  selectedItem: string | number | undefined;
  openPopup: () => void;
};

const CategoryWrapper = styled(Box)(({theme}) => ({
  width: '100%',
  marginTop: '50px',
  minHeight: '600px',
  [theme.breakpoints.down('lg')]: {
    minHeight: '500px',
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '575px',
  },
}));

const CategoryItem = styled(Paper)(({theme}) => ({
  width: '100%',
  height: '157px',
  backgroundColor: 'theme.palette.background.paper',
  position: 'relative',
  boxShadow: 'none',
  overflow: 'visible',
  [theme.breakpoints.down('md')]: {
    height: 'auto',
  },
}));

const CategoryIcon = styled(Image)(() => ({
  width: '52px',
  height: '52px',
}));

const PersonalizeHeader = ({
  isMobile,
  onSelect,
  selectedItem,
  openPopup,
}: PersonalizeHeaderType) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const CustomPrevArrow = (props: any) => {
    const {onClick, currentSlide} = props;
    const isDisabled = currentSlide === 0;
    const width1024 = useIsCustomWidth(1025);
    return (
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '2.5%',

          transform: 'translateY(-50%)',
          zIndex: 1,
          cursor: 'pointer',
        }}
        onClick={onClick}
      >
        <Image
          src={OrangeLeftArrowCircle}
          alt="orange-arrow"
          height={55}
          width={55}
          style={{
            paddingTop: '3px',
            marginLeft: width1024 ? '-55px' : '-62px',
            marginTop: '16px',
            display: isDisabled ? 'none' : 'block',
          }}
        />
      </div>
    );
  };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const CustomNextArrow = (props: any) => {
    const {onClick, currentSlide, slideCount} = props;
    const isDisabled = currentSlide + 6 >= slideCount;
    const width1024 = useIsCustomWidth(1025);
    return (
      <div
        style={{
          position: 'absolute',
          left: '97%',
          top: '50%',

          transform: 'translateY(-50%)',
          zIndex: 1,
          cursor: 'pointer',
        }}
        onClick={onClick}
      >
        <Image
          src={OrangeRightArrowCircle}
          alt="orange-arrow"
          height={55}
          width={55}
          style={{
            marginLeft: width1024 ? '5px' : '12px',
            paddingTop: '3px',
            marginTop: '16px',
            display: isDisabled ? 'none' : 'block',
          }}
        />
      </div>
    );
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 150,
    slidesToShow: useIsCustomWidth(1025) ? 5 : 6,
    slidesToScroll: 6,
    adaptiveHeight: true,
    arrows: true,
    autoplay: false,
    nextArrow: <CustomNextArrow />,
    prevArrow: <CustomPrevArrow />,
  };

  if (loading) {
    return;
  }
  return (
    <>
      <Box
        sx={{
          backgroundRepeat: 'no-repeat',
          backgroundImage: `url('https://job-static.hirist.com/V8/static/media/background-bg.ba8365c7.webp')`,
          width: '100%',
          display: 'inline-block',
          height: '210px',
          verticalAlign: 'top',
          backgroundColor: '#13355c',
          backgroundPositionY: '4px',
          paddingTop: '47px',
          paddingLeft: {xs: 2, md: 3.75, lg: 6.25},
          paddingRight: {xs: 2, md: 3.75, lg: 6.25},
        }}
      >
        <Box alignSelf="center">
          <Typography
            sx={{
              lineHeight: '46px',
              fontSize: '34px',
              color: '#ffffff',
              margin: 0,
              fontWeight: 200,
              '@media screen and (max-width: 1023px)': {
                fontSize: '31px',
              },
              '@media screen and (max-width: 599px)': {
                fontSize: '24px',
                lineHeight: '31px',
              },
            }}
          >
            Create your own Jobfeed, <b>Let&apos;s Get Started!!</b>
          </Typography>
        </Box>
        <CategoryWrapper>
          <Grid
            container
            spacing={2}
            rowSpacing={0.5}
            sx={{
              cursor: 'pointer',
              backgroundColor: 'white',
              boxShadow: '0 6px 20px 0 rgba(0, 0, 0, .06)',
              border: '1px solid #efefef',
              pb: {xxs: 4, md: 0},
              ml: '0px',
              width: '100%',
              borderRadius: '3px',
            }}
          >
            {!loading && !isMobile ? (
              <Box width="100%" ml={0}>
                <Slider {...settings}>
                  {categories
                    .filter(e => e.icon)
                    .map(category => (
                      <>
                        <CategoryItem
                          onClick={() => onSelect(category.id)}
                          key={category.id}
                          sx={{
                            borderRight: '1px solid #f5f5f5',
                            maxHeight: {xxs: '157px', md: '130px'},
                          }}
                        >
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                              paddingTop: {md: 1, lg: 2},
                              px: '15px',
                            }}
                            overflow={'visible'}
                            data-testid="category-item"
                          >
                            <CategoryIcon
                              src={category.icon}
                              alt={category.name}
                            />
                            <Typography
                              variant="body1"
                              height="23px"
                              pt={{md: '0px', lg: '10px'}}
                              textAlign={'center'}
                              fontFamily={'Nunito Sans'}
                              // fontWeight={700}
                              mb={{xxs: 4, md: 0}}
                              // maxWidth={{md: 160, lg: 'auto'}}
                              overflow={'visible'}
                              color={
                                selectedItem === category.id
                                  ? 'rgb(233, 99, 12)'
                                  : 'textPrimary'
                              }
                            >
                              {category.name}
                            </Typography>
                          </Box>
                          {selectedItem === category.id && !isMobile && (
                            <Box
                              position="absolute"
                              width="100%"
                              height="4px"
                              bgcolor="#e9630c"
                              borderRadius="3px"
                              justifyContent="center"
                              sx={{
                                display: {xxs: 'none', md: 'flex'},
                              }}
                              top={126} // Alternative to top={54} if needed
                            >
                              <Box
                                position="absolute"
                                width="18px"
                                height="18px"
                                bgcolor="#e9630c"
                                borderRadius="4px"
                                display="block" // Ensures it renders properly
                                sx={{
                                  transform: 'rotate(-45deg)',
                                  clipPath: 'polygon(0 0, 100% 100%, 0 100%)',
                                  marginTop: '-9px',
                                }}
                              />
                            </Box>
                          )}
                        </CategoryItem>
                      </>
                    ))}
                </Slider>
              </Box>
            ) : (
              <>
                {!loading &&
                  categories.map(category => (
                    <>
                      <Grid
                        item
                        xxs={12}
                        xs={6}
                        sm={4}
                        md={2}
                        key={category.id}
                        sx={{
                          m: 0,
                          p: 0,
                          pt: '0 !important',
                          pl: '0 !important',
                          border: '1px solid #f5f5f5',
                          height: '157px',
                        }}
                      >
                        <CategoryItem
                          onClick={() => {
                            onSelect(category.id);
                            openPopup();
                          }}
                          key={category.id}
                        >
                          <Box
                            textAlign="center"
                            sx={{
                              px: 1.875,
                              lineHeight: '1.1px',
                            }}
                          >
                            <CategoryIcon
                              sx={{
                                marginTop: '27px',
                                marginBottom: '13px',
                              }}
                              src={category.icon}
                              alt={category.name}
                            />
                            <Typography
                              variant="body1"
                              height="23px"
                              paddingTop="0px"
                              fontFamily={'Nunito Sans'}
                              sx={{
                                lineHeight: '18px',
                                fontSize: '16px',
                                textAlign: 'center',
                                color: '#000',
                                margin: 0,
                                overflowWrap: 'break-word',
                              }}
                              mb={0}
                              color={
                                selectedItem === category.id
                                  ? 'rgb(233, 99, 12)'
                                  : 'textPrimary'
                              }
                            >
                              {category.name}
                            </Typography>
                          </Box>
                        </CategoryItem>
                      </Grid>
                    </>
                  ))}
              </>
            )}
          </Grid>
        </CategoryWrapper>
      </Box>
    </>
  );
};

export default PersonalizeHeader;
